import { useRef, useState, useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Quote, Heading2, Heading3, Link2, Image as ImageIcon, Table, AlertCircle, Undo, Redo } from "lucide-react";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { resolveImg } from "@/lib/util";

export function RichTextEditor({ value, onChange }) {
  const ref = useRef();
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, []); // init only

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (command, arg = null) => { document.execCommand(command, false, arg); ref.current.focus(); emit(); };

  const addLink = () => { const url = prompt("Enter URL:"); if (url) cmd("createLink", url); };
  const addTable = () => {
    const html = '<table><thead><tr><th>Heading</th><th>Heading</th></tr></thead><tbody><tr><td>Cell</td><td>Cell</td></tr><tr><td>Cell</td><td>Cell</td></tr></tbody></table><p></p>';
    cmd("insertHTML", html);
  };
  const addCallout = () => cmd("insertHTML", '<div class="callout"><strong>Note:</strong> Add your callout text here.</div><p></p>');
  const insertImage = (m) => cmd("insertHTML", `<img src="${resolveImg(m.url)}" alt="${m.alt || ""}" />${m.caption ? `<p style="text-align:center;font-size:0.85rem;color:#64748b">${m.caption}</p>` : ""}<p></p>`);

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className="w-9 h-9 flex items-center justify-center rounded-sm text-slate-600 hover:bg-navy hover:text-ivory transition-colors" data-testid={testid}>
      {children}
    </button>
  );

  return (
    <div className="border border-slate-300 rounded-sm overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 bg-slate-100 border-b border-slate-200 p-1.5">
        <Btn onClick={() => cmd("formatBlock", "<h2>")} title="Heading 2" testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<h3>")} title="Heading 3" testid="rte-h3"><Heading3 size={16} /></Btn>
        <span className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("bold")} title="Bold" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => cmd("italic")} title="Italic" testid="rte-italic"><Italic size={16} /></Btn>
        <span className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("insertUnorderedList")} title="Bullet list" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} title="Numbered list" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<blockquote>")} title="Quote" testid="rte-quote"><Quote size={16} /></Btn>
        <span className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={addLink} title="Link" testid="rte-link"><Link2 size={16} /></Btn>
        <Btn onClick={() => setPicker(true)} title="Insert image" testid="rte-image"><ImageIcon size={16} /></Btn>
        <Btn onClick={addTable} title="Table" testid="rte-table"><Table size={16} /></Btn>
        <Btn onClick={addCallout} title="Callout box" testid="rte-callout"><AlertCircle size={16} /></Btn>
        <span className="w-px h-6 bg-slate-300 mx-1" />
        <Btn onClick={() => cmd("undo")} title="Undo"><Undo size={16} /></Btn>
        <Btn onClick={() => cmd("redo")} title="Redo"><Redo size={16} /></Btn>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onBlur={emit}
        className="prose-legal min-h-[320px] max-h-[560px] overflow-y-auto p-5 focus:outline-none bg-white" data-testid="rte-content" />
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={insertImage} type="image" />
    </div>
  );
}
